import React, { useState, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  Stack,
  FormControl,
  FormHelperText,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  Flex,
  Link,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  IconButton,
  CircularProgress,
  useToast,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Collapse,
} from "@chakra-ui/react";
import Cropper from "react-easy-crop";
import { FaMoneyBills } from "react-icons/fa6";
import { FaUpload, FaChevronDown, FaTimes, FaSync, FaImage, FaExclamationCircle } from "react-icons/fa";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { getFormError } from "../../../i18n";
import NextImage from "next/image";
import DeleteFileIcon from "../../../assets/delete-file-btn.svg";
import { PixelGrid } from "../../../components/PixelGrid";
import { useUploadGuessImage } from "../../../hooks/useUploadGuessImage";
import MinAmountInfo from "../../../components/MinAmountInfo";

// Helper to get cropped image blob from canvas
const getCroppedImg = async (imageSrc, pixelCrop) => {
  const image = await new Promise((resolve, reject) => {
    const img = new window.Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (err) => reject(err));
    img.src = imageSrc;
  });

  const canvas = document.createElement("canvas");
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, "image/jpeg");
  });
};

// Pixelated overlay grid for "Gambar Tebakan" preview

// Image upload + crop modal - renderless version (no UI box)
const ImageCropModal = ({ onCropComplete: onDone, onError, onErrorFile, triggerRef }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [rawSrc, setRawSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const [uploadGuessImage] = useUploadGuessImage({
    reactQueryProps: {
      onSuccess: (data) => {
        toast({ title: "Gambar berhasil diupload", status: "success", duration: 3000, isClosable: true });
      },
      onError: (error) => {
        onError && onError();
        toast({ title: "Gagal upload gambar", description: "Coba lagi ya!", status: "error", duration: 5000, isClosable: true });
      },
    },
  });

  React.useImperativeHandle(triggerRef, () => ({
    trigger: () => { if (fileInputRef.current) fileInputRef.current.click(); }
  }));

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const MAX_SIZE_BYTES = 10 * 1024 * 1024;
    const SUPPORTED_FORMATS = ["image/jpeg", "image/jpg", "image/png"];
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      toast({ title: "Format tidak didukung", description: "Ukuran file maksimal 10 MB. Format yang didukung: JPG, JPEG, PNG", status: "error", duration: 5000, isClosable: true });
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast({ title: "Ukuran file terlalu besar", description: "Ukuran file maksimal 10 MB. Format yang didukung: JPG, JPEG, PNG", status: "error", duration: 5000, isClosable: true });
      return;
    }
    onErrorFile && onErrorFile(file);
    const reader = new FileReader();
    reader.onload = () => { setRawSrc(reader.result); onOpen(); };
    reader.onerror = () => {
      onError && onError();
      toast({ title: "Gagal membaca gambar", description: "Coba lagi dengan file lain.", status: "error", duration: 5000, isClosable: true });
    };
    reader.readAsDataURL(file);
  };

  const onCropCompleteInternal = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    setIsProcessing(true);
    try {
      const blob = await getCroppedImg(rawSrc, croppedAreaPixels);
      const file = new File([blob], "guess-image.jpg", { type: "image/jpeg" });
      
      const uploadResult = await uploadGuessImage({ imageFile: file });
      
      const reader = new FileReader();
      reader.onload = () => { 
        onDone(reader.result, blob, uploadResult?.file_path); 
        onClose(); 
        setIsProcessing(false); 
      };
      reader.onerror = () => { 
        onError && onError(); 
        setIsProcessing(false); 
        toast({ title: "Gagal memproses gambar", description: "Coba lagi ya!", status: "error", duration: 5000, isClosable: true }); 
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      console.error(e);
      onError && onError();
      setIsProcessing(false);
      onClose();
    }
  };

  return (
    <>
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/jpg,image/png" style={{ display: "none" }} onChange={onFileChange} />
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            bg="#EAEFED"
            borderBottom="1px black solid"
            py="10px"
            px="20px"
            borderTopLeftRadius="md"
            borderTopRightRadius="md"
          >
            <Text
              color="black"
              fontSize="18px"
              fontFamily="IBM Plex Mono"
              fontWeight="700"
              lineHeight="24px"
            >
              Crop Gambar
            </Text>
          </ModalHeader>
          <ModalBody px="16px" py="16px">
            <Flex direction="column" align="center" justify="center" gap="10px">
              <Box
                width="100%"
                height="390px"
                position="relative"
                background="white"
                overflow="hidden"
                borderRadius="6px"
              >
                {rawSrc && (
                  <Cropper
                    image={rawSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={4 / 3}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropCompleteInternal}
                    style={{
                      containerStyle: { borderRadius: "6px" },
                    }}
                  />
                )}
              </Box>
            </Flex>
            <Box
              borderTop="1px black solid"
              px="20px"
              py="13px"
              bg="white"
              mt="16px"
              mx="-16px"
            >
              <HStack spacing="12px" width="100%">
                <Text
                  fontFamily="IBM Plex Mono"
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="20px"
                  color="black"
                  whiteSpace="nowrap"
                >
                  Zoom:
                </Text>
                <Slider
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(val) => setZoom(val)}
                  flex={1}
                >
                  <SliderTrack
                    height="8px"
                    borderRadius="full"
                    bg="#D0D0D0"
                  >
                    <SliderFilledTrack bg="#3182CE" />
                  </SliderTrack>
                  <SliderThumb
                    width="19px"
                    height="19px"
                    bg="#3182CE"
                    border="1px solid black"
                    _focus={{ boxShadow: "none" }}
                  />
                </Slider>
              </HStack>
            </Box>
          </ModalBody>
          <ModalFooter
            borderTop="1px black solid"
            px="20px"
            py="16px"
            justifyContent="space-between"
          >
            <Button
              onClick={onClose}
              fontFamily="IBM Plex Mono"
              fontSize="18px"
              fontWeight="400"
              lineHeight="28px"
              color="black"
              bg="white"
              boxShadow="6.4px 6.4px 0px #222222"
              borderRadius="6px"
              outline="1px black solid"
              outlineOffset="-1px"
              px="20px"
              py="9px"
              height="auto"
              _hover={{ bg: "white" }}
              _active={{ boxShadow: "none", transform: "translate(6px, 6px)" }}
            >
              Batal
            </Button>
            <Button
              onClick={handleSave}
              fontFamily="IBM Plex Mono"
              fontSize="18px"
              fontWeight="400"
              lineHeight="28px"
              color="black"
              bg="#8BD3DD"
              boxShadow="6.4px 6.4px 0px #222222"
              borderRadius="6px"
              outline="1px black solid"
              outlineOffset="-1px"
              px="20px"
              py="9px"
              height="auto"
              isLoading={isProcessing}
              _hover={{ bg: "#8BD3DD" }}
              _active={{ boxShadow: "none", transform: "translate(6px, 6px)" }}
            >
              Simpan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

// Image upload + crop modal with UI box (empty state)
const ImageUploadCrop = ({ label, onCropComplete: onDone, onError, onErrorFile }) => {
  const modalRef = useRef(null);
  const [isProcessing] = useState(false);

  return (
    <>
      <ImageCropModal
        onCropComplete={onDone}
        onError={onError}
        onErrorFile={onErrorFile}
        triggerRef={modalRef}
      />
      <Box
        width="100%"
        height="240px"
        background="white"
        borderRadius="6px"
        outline="2px black solid"
        outlineOffset="-2px"
        padding="12px"
        display="flex"
        flexDirection="column"
        cursor="pointer"
        onClick={() => modalRef.current?.trigger()}
        position="relative"
      >
        <Flex direction="column" align="center" justify="center" width="100%" height="100%" gap={2}>
          <FaUpload size={24} color="#929393" />
          <Text fontFamily="IBM Plex Mono" fontSize="14px" color="#929393" textAlign="center">
            Klik untuk upload gambar
          </Text>
        </Flex>
      </Box>
    </>
  );
};

// Main ImageGuessSelector component
const ImageGuessSelector = ({ overlay, values, setFieldValue, setFieldError, currency, errors, touched }) => {
  const { _ } = useLingui();
  const {
    min_media: minimumMedia,
    min_image_guess: minimumImageGuess,
  } = overlay;

  const minAmount = minimumImageGuess || minimumMedia;
  const enabled = Number(values.amount) >= minAmount;

  const [originalPreview, setOriginalPreview] = useState(null);
  const [uploadError, setUploadError] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [answer, setAnswer] = useState("");
  const [isHoveringOriginal, setIsHoveringOriginal] = useState(false);
  const replaceImageRef = useRef(null);
  const MAX_ANSWER_LENGTH = 20;

  const handleOriginalCrop = (base64, blob, filePath) => {
    setOriginalPreview(base64);
    setUploadError(false);
    setFieldValue("image_guess", filePath ?? null);
  };

  const handleUploadError = () => setUploadError(true);
  const handleErrorFile = (file) => setSelectedFile(file);

  const handleRemove = (e) => {
    e && e.stopPropagation();
    setOriginalPreview(null);
    setUploadError(false);
    setSelectedFile(null);
    setFieldValue("image_guess", null);
  };

  const handleAnswerChange = (e) => {
    const val = e.target.value.slice(0, MAX_ANSWER_LENGTH);
    setAnswer(val);
    setFieldValue("image_guess_answer", val);
  };

  const hintText = answer.replace(/[aiueoAIUEO]/g, "_");

  const [showCaraMain, setShowCaraMain] = useState(false);

  if (!enabled) {
    return (
      <MinAmountInfo
        labels={["Min. image guess:"]}
        amounts={[minAmount]}
        currency={currency}
        reactIcon={FaMoneyBills}
        mt={0}
        mb={5}
      />
    );
  }

  return (
    <Box
      width="100%"
      padding="20px"
      background="#EAEFED"
      borderRadius="6.39px"
      outline="1.07px #BEC1C1 solid"
      outlineOffset="-1.07px"
      display="flex"
      flexDirection="column"
      gap="16px"
    >
      {/* Header */}
      <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} width="100%" gap={{ base: "4px", md: "0" }}>
        <Text fontFamily="IBM Plex Mono" fontSize="16px" fontWeight="500" lineHeight="24px" color="black" flex={1}>
          🎮 Tantang streamer tebak gambar-mu!
        </Text>
        <HStack spacing="4px">
          <Link
            href="#"
            fontFamily="IBM Plex Mono"
            fontSize="14px"
            fontWeight="400"
            lineHeight="20px"
            color="#3182CE"
            textDecoration="none"
            _hover={{ textDecoration: "underline" }}
            onClick={(e) => { e.preventDefault(); setShowCaraMain((v) => !v); }}
          >
            Lihat cara main
          </Link>
          <FaChevronDown size={16} color="#3182CE" />
        </HStack>
      </Flex>

      {/* Cara Main Alert */}
      <Collapse in={showCaraMain} animateOpacity>
        <Box
          width="100%"
          px="16px"
          py="12px"
          bg="#DCEEFF"
          borderRadius="6px"
          outline="1px solid #3182CE"
          outlineOffset="-1px"
          display="flex"
          flexDirection="row"
          alignItems="flex-start"
          gap="12px"
          overflow="hidden"
          my="10px"
        >
          {/* Info Icon */}
          <Box width="30px" height="20px" flexShrink={0} position="relative" overflow="hidden">
            <FaExclamationCircle size={20} color="#3182CE" />
          </Box>

          {/* Content */}
          <Flex flex={1} direction="column" gap="8px">
            <Text
              color="black"
              fontSize="16px"
              fontFamily="IBM Plex Mono"
              fontWeight="700"
              lineHeight="24px"
              wordBreak="break-word"
            >
              Cara main Tebak Gambar:
            </Text>
            <Text
              as="ul"
              color="black"
              fontSize="16px"
              fontFamily="IBM Plex Mono"
              fontWeight="400"
              lineHeight="24px"
              wordBreak="break-word"
              paddingLeft="20px"
            >
              <Box as="li">Gambar akan muncul di live streaming dengan beberapa bagian yang tertutup.</Box>
              <Box as="li">Streamer harus menebak gambar sebelum waktunya habis.</Box>
              <Box as="li">Setelah waktu habis, gambar asli dan jawabannya akan ditampilkan</Box>
            </Text>
          </Flex>
        </Box>
      </Collapse>

      {/* Image Section */}
      {originalPreview && !uploadError ? (
        // Success state: side by side with labels
        <Stack
          direction={{ base: "column", md: "row" }}
          spacing="16px"
          width="100%"
          alignItems="flex-start"
          my="10px"
        >
          {/* Gambar Asli */}
          <Flex direction="column" gap="12px" flex={1}>
            <Text fontFamily="IBM Plex Mono" fontSize="14px" fontWeight="500" lineHeight="20px" color="black" mb="10px">
              Gambar Asli
            </Text>
            <Box
              width="100%"
              borderRadius="6px"
              outline="2px black solid"
              outlineOffset="-2px"
              position="relative"
              background="white"
              padding="12px"
              cursor="pointer"
              onMouseEnter={() => setIsHoveringOriginal(true)}
              onMouseLeave={() => setIsHoveringOriginal(false)}
              onClick={() => replaceImageRef.current?.trigger()}
            >
              {/* Hidden modal for replacing image - no UI box */}
              <ImageCropModal
                onCropComplete={handleOriginalCrop}
                onError={handleUploadError}
                onErrorFile={handleErrorFile}
                triggerRef={replaceImageRef}
              />
              <Box width="100%" height="220px" position="relative" overflow="hidden" borderRadius="4.26px">
                <Image src={originalPreview} alt="Gambar Asli" objectFit="cover" width="100%" height="100%" borderRadius="4px" />
                {isHoveringOriginal && (
                  <Flex position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0,0,0,0.50)" direction="column" align="center" justify="center" gap="12px" borderRadius="4.26px">
                    <FaImage size={30} color="white" />
                    <Text fontFamily="IBM Plex Mono" fontSize="14px" fontWeight="600" lineHeight="20px" color="white" textAlign="center">
                      Klik untuk ganti gambar
                    </Text>
                  </Flex>
                )}
              </Box>
            </Box>
          </Flex>

          {/* Gambar Tebakan */}
          <Flex direction="column" gap="12px" width={{ base: "100%", md: "100%" }} flex={{ base: "none", md: 1 }}>
            <Text fontFamily="IBM Plex Mono" fontSize="14px" fontWeight="500" lineHeight="20px" color="black" mb="10px">
              Gambar Tebakan
            </Text>
            <Box width="100%" padding="12px" background="white" borderRadius="6px" outline="1px black solid" outlineOffset="-1px">
              <Box width="100%" height="220px" position="relative" overflow="hidden" borderRadius="4px">
                <Image src={originalPreview} alt="Gambar Tebakan" objectFit="cover" width="100%" height="100%" borderRadius="4px" position="absolute" top={0} left={0} />
                <PixelGrid cols={6} rows={8} />
              </Box>
            </Box>
          </Flex>
        </Stack>
      ) : uploadError && selectedFile ? (
        // Error state: full width gray box
        <Box
          width="100%"
          height="240px"
          borderRadius="8px"
          overflow="hidden"
          position="relative"
          cursor="pointer"
          onClick={() => setUploadError(false)}
          bg="rgba(0,0,0,0.50)"
          my="10px"
        >
          <Flex direction="column" align="center" justify="center" position="absolute" top={0} left={0} right={0} bottom={0} gap="8px" zIndex={2}>
            <FaSync size={32} color="white" />
            <Text fontFamily="IBM Plex Mono" fontSize="16px" fontWeight="500" lineHeight="24px" color="white">
              Coba Lagi
            </Text>
          </Flex>
          <Box
            position="absolute" top={0} right={0}
            width="46px" height="46px"
            bg="#FFE3E6"
            borderTopRightRadius="8px"
            borderBottomLeftRadius="8px"
            display="flex" alignItems="center" justifyContent="center"
            zIndex={3}
            onClick={(e) => { e.stopPropagation(); handleRemove(e); }}
            cursor="pointer"
          >
            <NextImage src={DeleteFileIcon} alt="Hapus File" width={40} height={40} color="#E53E3E" />
          </Box>
        </Box>
      ) : (
        // Empty state: full width upload box
        <Box my="10px">
          <ImageUploadCrop
            label="Gambar Asli"
            onCropComplete={handleOriginalCrop}
            onError={handleUploadError}
            onErrorFile={handleErrorFile}
          />
          {errors?.image_guess && (
            <Text fontFamily="IBM Plex Mono" fontSize="14px" color="#E53E3E" mt="8px">
              {getFormError(errors.image_guess, _)}
            </Text>
          )}
        </Box>
      )}

      {/* Answer + Hint side by side - only show when image uploaded successfully */}
      {originalPreview && !uploadError && (
        <Stack
          direction={{ base: "column", md: "row" }}
          spacing="16px"
          width="100%"
          alignItems="flex-start"
          my="10px"
        >
          {/* Jawaban */}
          <FormControl flex={1} isInvalid={errors?.image_guess_answer}>
            <FormLabel fontFamily="IBM Plex Mono" fontSize="16px" fontWeight="500" lineHeight="24px" color="black" mb="8px">
              Jawaban yang Benar:{" "}
              <Box as="span" color="#E53E3E">*</Box>
            </FormLabel>
            <Box borderBottom="1px black solid" py="8px">
              <Input
                variant="unstyled"
                fontFamily="IBM Plex Mono"
                fontSize="16px"
                fontWeight="400"
                lineHeight="24px"
                placeholder="Contoh: Kucing"
                _placeholder={{ color: "#A8AAAA" }}
                value={answer}
                onChange={handleAnswerChange}
                maxLength={MAX_ANSWER_LENGTH}
              />
            </Box>
            <Flex justify="space-between" mt="8px">
              <FormErrorMessage fontFamily="IBM Plex Mono" fontSize="14px" lineHeight="20px">
                {getFormError(errors?.image_guess_answer, _)}
              </FormErrorMessage>
              <Text fontFamily="IBM Plex Mono" fontSize="14px" color="#929393" lineHeight="20px">{answer.length}/{MAX_ANSWER_LENGTH}</Text>
            </Flex>
          </FormControl>

          {/* Hint untuk streamer */}
          <FormControl flex={1}>
            <FormLabel fontFamily="IBM Plex Mono" fontSize="16px" fontWeight="500" lineHeight="24px" color="black" mb="8px">
              Hint untuk streamer
            </FormLabel>
            <Box
              width="100%" paddingY="10px" paddingX="12px"
              background="#F2F7F5" borderRadius="6px"
              outline="1px #BEC1C1 solid" outlineOffset="-1px"
              display="flex" alignItems="center" justifyContent="center"
              minHeight="44px"
            >
              <Text fontFamily="IBM Plex Mono" fontSize="12px" fontWeight="400" lineHeight="20px" color={answer ? "black" : "#929393"} textAlign="center">
                {answer ? hintText : "akan muncul disini"}
              </Text>
            </Box>
          </FormControl>
        </Stack>
      )}

      {/* Helper text */}
      <FormControl width="100%">
        <FormHelperText fontFamily="IBM Plex Mono">
          <Trans>Upload gambar yang akan ditebak oleh streamer dan berikan jawaban yang benar.</Trans>
        </FormHelperText>
      </FormControl>
    </Box>
  );
};

export { ImageGuessSelector };